import { Dayjs } from "utils/dayjs"

import { Guid, IHydratable, ITranslations } from "@/types"
import { printPeriod, toDayjsWithoutTimezone } from "utils/utils"
import { AssetOwnershipType } from "./Asset"
import AssetDisplayStatus from "./AssetDisplayStatus"
import Location from "./Location"
import { ShippingOrderStatus, ShippingOrderType } from "./ShippingOrder"
import ShippingOrderAsset, { AssetShippingStatus } from "./ShippingOrderAsset"

export interface IGetReservationAssetDto {
    assetId?: Guid
    hasUserAssignment: boolean
    start?: string | null
    end?: string | null
}

export default class ReservationAsset implements IHydratable {
    assetId?: Guid
    hasUserAssignment: boolean
    start?: Dayjs
    end?: Dayjs

    articleId?: Guid
    articleNumber?: string
    articleDescription?: string
    altArticleDescription?: string
    articleDescriptionTranslations?: ITranslations
    articleThumbnailUri?: string
    articleFeatures?: ITranslations
    serialNumber?: string
    lmid?: string
    isLabeled?: boolean
    isOperational?: boolean
    location?: Location
    warehouseId?: Guid
    profitCenterId?: Guid
    features?: ITranslations
    hasFeaturesOrFreightUnits?: boolean
    unplanned?: boolean
    shipping?: {
        shippingOrderType: ShippingOrderType
        shippingOrderStatus: ShippingOrderStatus
        shippingOrderAsset: ShippingOrderAsset
    }[]
    colorId?: Guid
    constructionYear?: number
    homebaseId?: Guid
    ownershipType?: AssetOwnershipType
    stateOfStorage?: AssetDisplayStatus
    customerName?: string
    assetCollectionId?: Guid

    constructor(dto: IGetReservationAssetDto) {
        this.assetId = dto.assetId || undefined
        this.hasUserAssignment = dto.hasUserAssignment || false
        this.start = dto.start ? toDayjsWithoutTimezone(dto.start) : undefined
        this.end = dto.end ? toDayjsWithoutTimezone(dto.end) : undefined
    }

    get isFixed(): boolean {
        return this.hasUserAssignment || !!this.unplanned
    }

    get currentShippingStatus(): AssetShippingStatus | null {
        if (!this.shipping || this.shipping.length === 0) return null
        let latestDate: Dayjs | null = null
        let latestStatus: AssetShippingStatus | null = null

        for (const s of this.shipping) {
            const shippingOrderAsset = s.shippingOrderAsset
            if (!shippingOrderAsset.storeDate && !shippingOrderAsset.releaseDate) continue

            if (shippingOrderAsset.releaseDate && shippingOrderAsset.storeDate) {
                const isStoredAfterRelease = shippingOrderAsset.storeDate.isAfter(shippingOrderAsset.releaseDate)
                if (isStoredAfterRelease) {
                    if (!latestDate || shippingOrderAsset.storeDate.isAfter(latestDate)) {
                        latestDate = shippingOrderAsset.storeDate
                        latestStatus = AssetShippingStatus.stored
                    }
                } else {
                    if (!latestDate || shippingOrderAsset.releaseDate.isAfter(latestDate)) {
                        latestDate = shippingOrderAsset.releaseDate
                        latestStatus = AssetShippingStatus.released
                    }
                }
            }

            if (shippingOrderAsset.releaseDate && !shippingOrderAsset.storeDate) {
                if (!latestDate || shippingOrderAsset.releaseDate.isAfter(latestDate)) {
                    latestDate = shippingOrderAsset.releaseDate
                    latestStatus = AssetShippingStatus.released
                }
            } else if (!shippingOrderAsset.releaseDate && shippingOrderAsset.storeDate) {
                if (!latestDate || shippingOrderAsset.storeDate.isAfter(latestDate)) {
                    latestDate = shippingOrderAsset.storeDate
                    latestStatus = AssetShippingStatus.stored
                }
            }
        }

        return latestStatus
    }

    get isReleasedOrStored(): boolean {
        if (!this.shipping || this.shipping.length === 0) return false
        return this.shipping.some(s => s.shippingOrderAsset.isReleasedOrStored)
    }

    get isPushedToStock(): boolean {
        if (!this.shipping || this.shipping.length === 0) return false
        return this.shipping.some(s => s.shippingOrderAsset.isPushedToStock)
    }

    get isForwardedToShipping(): boolean {
        if (!this.shipping || this.shipping.length === 0) return false
        return this.shipping.some(s => s.shippingOrderAsset.isForwardedToShipping)
    }

    get isAvailable(): boolean {
        return !!this.assetId
    }

    // check whether the asset information is filled into asset if necessary
    get isHydrated(): boolean {
        return !this.assetId || !!this.lmid
    }

    get linkedToAssetCollection(): boolean {
        return !!this.assetCollectionId
    }

    printPeriod(params: {
        reservationStart?: Dayjs | null
        reservationEnd?: Dayjs | null
    }): string {
        return printPeriod({
            start: this.start || params.reservationStart,
            end: this.end || params.reservationEnd,
        })
    }
}
